'use client';

import { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { clamp } from './ui.video-player.helpers';
import { TrimRange } from './ui.video-player.types';

const MIN_TRIM_DURATION = 1;

interface Props {
  trimRange: TrimRange;
  duration: number;
  timelineHeight: number;
  timelineRef: React.RefObject<HTMLDivElement | null>;
  onTrimRangeChange: (range: TrimRange) => void;
}

const HighlightBar = styled.div<{ left: number; width: number }>`
  left: ${(props) => props.left}%;
  width: ${(props) => props.width}%;
  transform: translateZ(0);
`;

const Handle = styled.div<{ left: number; height: number }>`
  left: ${(props) => props.left}%;
  top: 0;
  height: ${(props) => props.height}px;
  transform: translateX(-50%);
`;

const stopClickPropagation = (e: React.MouseEvent) => e.stopPropagation();

export default function TimelineTrimRange({
  trimRange,
  duration,
  timelineHeight,
  timelineRef,
  onTrimRangeChange,
}: Props) {
  const dragEdgeRef = useRef<'left' | 'right' | null>(null);
  const dragStartRef = useRef<{ startTime: number; endTime: number } | null>(
    null
  );
  const [dragState, setDragState] = useState<'left' | 'right' | null>(null);
  const [dragPreview, setDragPreview] = useState<TrimRange | null>(null);

  const timeToPercent = (time: number) =>
    duration > 0 ? (time / duration) * 100 : 0;

  const percentToTime = (percent: number) =>
    duration * clamp(percent / 100, 0, 1);

  const handlePointerDown = (edge: 'left' | 'right', e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setDragState(edge);
    setDragPreview(null);
    dragEdgeRef.current = edge;
    dragStartRef.current = {
      startTime: trimRange.startTime,
      endTime: trimRange.endTime,
    };
    if (e.target instanceof HTMLElement)
      e.target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const edge = dragEdgeRef.current;
    if (!edge || !timelineRef.current || !dragStartRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = clamp((x / rect.width) * 100, 0, 100);
    const newTime = percentToTime(percent);

    const { startTime, endTime } = dragStartRef.current;

    if (edge === 'left') {
      const maxStart = endTime - MIN_TRIM_DURATION;
      const clampedStart = clamp(newTime, 0, maxStart);
      setDragPreview({
        startTime: clampedStart,
        endTime,
      });
    } else {
      const minEnd = startTime + MIN_TRIM_DURATION;
      const maxEnd = duration;
      const clampedEnd = clamp(newTime, minEnd, maxEnd);
      setDragPreview({
        startTime,
        endTime: clampedEnd,
      });
    }
  };

  const dragPreviewRef = useRef<TrimRange | null>(null);

  useEffect(() => {
    dragPreviewRef.current = dragPreview;
  }, [dragPreview]);

  const handlePointerUp = (e: React.PointerEvent) => {
    const edge = dragEdgeRef.current;
    const start = dragStartRef.current;
    if (e.target instanceof HTMLElement)
      e.target.releasePointerCapture(e.pointerId);
    setDragState(null);
    setDragPreview(null);
    dragPreviewRef.current = null;
    dragEdgeRef.current = null;
    dragStartRef.current = null;

    if (edge && start && timelineRef.current) {
      const rect = timelineRef.current?.getBoundingClientRect();
      if (rect) {
        const x = e.clientX - rect.left;
        const percent = clamp((x / rect.width) * 100, 0, 100);
        const newTime = percentToTime(percent);
        if (edge === 'left') {
          const maxStart = start.endTime - MIN_TRIM_DURATION;
          const clampedStart = clamp(newTime, 0, maxStart);
          onTrimRangeChange({
            startTime: clampedStart,
            endTime: start.endTime,
          });
        } else {
          const minEnd = start.startTime + MIN_TRIM_DURATION;
          const maxEnd = duration;
          const clampedEnd = clamp(newTime, minEnd, maxEnd);
          onTrimRangeChange({
            startTime: start.startTime,
            endTime: clampedEnd,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (!dragState) return;

    const handleGlobalPointerUp = () => {
      const final = dragPreviewRef.current;
      if (final) onTrimRangeChange(final);
      dragPreviewRef.current = null;
      setDragState(null);
      setDragPreview(null);
      dragEdgeRef.current = null;
      dragStartRef.current = null;
    };

    window.addEventListener('pointerup', handleGlobalPointerUp);
    return () => window.removeEventListener('pointerup', handleGlobalPointerUp);
  }, [dragState, onTrimRangeChange]);

  const displayRange = dragPreview ?? trimRange;
  const startPos = clamp(timeToPercent(displayRange.startTime), 0, 100);
  const endPos = clamp(timeToPercent(displayRange.endTime), 0, 100);
  const width = Math.max(0, Math.min(endPos - startPos, 100 - startPos));

  return (
    <>
      <div
        data-test='timeline-trim-overlay'
        className='absolute inset-0 rounded-md bg-gray-500'
        aria-hidden='true'
      />
      <HighlightBar
        data-test='timeline-trim-highlight'
        className='absolute top-0 z-10 h-full rounded-md border-y-2 border-white bg-indigo-400 transition-none will-change-transform'
        left={startPos}
        width={width}
        aria-label='Clip trim range'
      />
      <Handle
        data-test='timeline-trim-handle-left'
        className='absolute top-0 z-20 flex w-2 cursor-ew-resize items-center justify-center rounded-sm border-2 border-white bg-white/90 shadow-md hover:bg-white'
        left={startPos}
        height={timelineHeight}
        onClick={stopClickPropagation}
        onPointerDown={(e) => handlePointerDown('left', e)}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        aria-label='Adjust clip start time'
      >
        <div
          className='h-[calc(100%-8px)] w-px shrink-0 bg-black'
          aria-hidden='true'
        />
      </Handle>
      <Handle
        data-test='timeline-trim-handle-right'
        className='absolute top-0 z-20 flex w-2 cursor-ew-resize items-center justify-center rounded-sm border-2 border-white bg-white/90 shadow-md hover:bg-white'
        left={endPos}
        height={timelineHeight}
        onClick={stopClickPropagation}
        onPointerDown={(e) => handlePointerDown('right', e)}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        aria-label='Adjust clip end time'
      >
        <div
          className='h-[calc(100%-8px)] w-px shrink-0 bg-black'
          aria-hidden='true'
        />
      </Handle>
    </>
  );
}
